import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import type { TConversationHistoryState } from "../types/agent";

import Chat from "../components/agent/Chat";
import NewChat from "../components/agent/NewChat";
import Sidebar from "../components/agent/Sidebar";
import { Navigation } from "../components/Navigation";
import { AGENT_HISTORY_TURN_PAGE_SIZE } from "../constants/agent";
import { useReadOnlyAccess } from "../hooks/useReadOnlyAccess";
import { setSelectedConversation } from "../redux/agent/agent.slice";
import {
	getConversationMessagesById,
	getConversationTurnPage,
	getConversations,
	getAgentMeta,
} from "../redux/agent/agent.thunk";
import {
	useAppDispatch,
	useAppSelector,
} from "../redux/store";

const buildTurnIndexRange = (
	startIdx: number,
	endIdx: number
) => {
	if (endIdx < startIdx) {
		return [];
	}

	return Array.from(
		{ length: endIdx - startIdx + 1 },
		(_, offset) => startIdx + offset
	);
};

const getLatestTurnPage = (totalTurns: number) => {
	if (totalTurns <= 0) {
		return [];
	}

	const startIdx = Math.max(
		0,
		totalTurns - AGENT_HISTORY_TURN_PAGE_SIZE
	);
	return buildTurnIndexRange(startIdx, totalTurns - 1);
};

const getNextOlderTurnPage = (
	messageHistory: TConversationHistoryState
) => {
	if (messageHistory.loadedTurnIndices.length === 0) {
		return getLatestTurnPage(messageHistory.totalTurns);
	}

	const oldestLoadedTurnIdx = Math.min(
		...messageHistory.loadedTurnIndices
	);
	if (oldestLoadedTurnIdx <= 0) {
		return [];
	}

	const startIdx = Math.max(
		0,
		oldestLoadedTurnIdx - AGENT_HISTORY_TURN_PAGE_SIZE
	);
	return buildTurnIndexRange(
		startIdx,
		oldestLoadedTurnIdx - 1
	);
};

const Agent: React.FC = () => {
	const { conversationId } = useParams<{
		conversationId: string;
	}>();
	const {
		conversations,
		messages,
		loadingConversations,
		loadingMessages,
		selectedConversation,
		conversationsError,
		messagesError,
		messageHistory,
		loadedConversationId,
	} = useAppSelector((state) => state.agent);
	const routeConversation =
		selectedConversation?.conv_id === conversationId
			? selectedConversation
			: null;
	const routeMessages =
		loadedConversationId === conversationId ? messages : [];

	const [isSidebarCollapsed, setIsSidebarCollapsed] =
		useState(false);
	const dispatch = useAppDispatch();
	const isReadOnlyUser = useReadOnlyAccess();

	const toggleSidebar = () => {
		setIsSidebarCollapsed(!isSidebarCollapsed);
	};

	useEffect(() => {
		void dispatch(getConversations());
		void dispatch(getAgentMeta());
	}, [dispatch]);

	useEffect(() => {
		if (conversationId && conversations.length > 0) {
			const conversation = conversations.find(
				(c) => c.conv_id === conversationId
			);
			if (conversation) {
				dispatch(setSelectedConversation(conversation));
			}
		}
	}, [conversationId, conversations, dispatch]);

	useEffect(() => {
		if (conversationId) {
			let cancelled = false;

			const loadConversation = async () => {
				const result = await dispatch(
					getConversationMessagesById(conversationId)
				);

				if (
					cancelled ||
					!getConversationMessagesById.fulfilled.match(
						result
					)
				) {
					return;
				}

				const totalTurns = result.payload.num_turns ?? 0;
				const initialTurnPage =
					getLatestTurnPage(totalTurns);

				if (initialTurnPage.length === 0) {
					return;
				}

				await dispatch(
					getConversationTurnPage({
						conversationId,
						turnIndices: initialTurnPage,
						requestKind: "initial",
					})
				);
			};

			void loadConversation();

			return () => {
				cancelled = true;
			};
		}
	}, [conversationId, dispatch]);

	const handleLoadMoreMessages = async () => {
		if (!conversationId) {
			return;
		}

		const nextTurnPage = getNextOlderTurnPage(messageHistory);
		if (nextTurnPage.length === 0) {
			return;
		}

		await dispatch(
			getConversationTurnPage({
				conversationId,
				turnIndices: nextTurnPage,
				requestKind: "loadMore",
			})
		);
	};

	const handleRetryInitialMessages = async () => {
		if (!conversationId) {
			return;
		}

		const metadataResult = await dispatch(
			getConversationMessagesById(conversationId)
		);

		if (
			!getConversationMessagesById.fulfilled.match(
				metadataResult
			)
		) {
			return;
		}

		const initialTurnPage = getLatestTurnPage(
			metadataResult.payload.num_turns ?? 0
		);
		if (initialTurnPage.length === 0) {
			return;
		}

		await dispatch(
			getConversationTurnPage({
				conversationId,
				turnIndices: initialTurnPage,
				requestKind: "initial",
			})
		);
	};

	const handleRetryConversations = () => {
		void dispatch(getConversations());
	};

	return (
		<>
			<Navigation />
			<div className="agent-container">
				<Sidebar
					conversations={conversations}
					loading={loadingConversations}
					isCollapsed={isSidebarCollapsed}
					toggleSidebar={toggleSidebar}
					selectedConversation={selectedConversation}
					error={conversationsError}
					onRetryConversations={handleRetryConversations}
					readOnly={isReadOnlyUser}
				/>
				<div
					className={`agent-main-content ${
						isSidebarCollapsed ? "collapsed" : ""
					}`}
				>
					{conversationId ? (
						<Chat
							key={conversationId}
							conversationId={conversationId}
							selectedConversation={routeConversation}
							messages={routeMessages}
							loading={loadingMessages}
							error={messagesError}
							messageHistory={messageHistory}
							onLoadMoreMessages={
								handleLoadMoreMessages
							}
							onRetryInitialMessages={
								handleRetryInitialMessages
							}
							readOnly={isReadOnlyUser}
						/>
					) : (
						<NewChat readOnly={isReadOnlyUser} />
					)}
				</div>
			</div>
		</>
	);
};

export default Agent;
